// Renders an assistant message as Markdown + special-cases the
// `loom-confirm` fenced code block into an Accept-changes button.
//
// The contract with the agent (PM-mode directive + servant prompt):
// when the agent drafts a proposed write — refining an issue, etc. — it
// ends its message with
//
//     ```loom-confirm
//     Update issue #11 with the refined description and clear loom::needs-refinement.
//     ```
//
// We replace the rendered code block with a button labelled "Accept
// changes" (the block body becomes the tooltip / subtitle). Click sends
// an affirmative chat reply via the `onAcceptChanges` callback (wired
// to `useWsSession.send`), which is the agent's cue to call
// `mcp__loom__apply_update_issue` and actually perform the write.
//
// Multiple `loom-confirm` blocks in one message all render as
// independent buttons. Click disables the local button and surfaces
// "Sent" so the user knows the affirmative reply went out — there's no
// global "consumed" state because the agent's response is what closes
// the loop.
//
// The `sent` latch is remount-safe (keyed by the entry id + block summary):
// the reconnect timeline wipe unmounts every timeline entry and replays it,
// and a plain useState latch would re-arm the button — a one-shot write
// affordance resurrected as clickable is a double-send hazard, not just lost
// state.

import { type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { uiProse } from "@loom/shared/brand";

import { useT } from "../../i18n/index.js";
import { useRemountSafeState } from "../../state/remount-safe-state";
import { CONFIRM_BTN } from "../../ui/classes";

const ACCEPT_REPLY = "Yes — apply the changes.";

interface AcceptButtonProps {
  readonly summary: string;
  readonly stateKey: string | null;
  readonly onClick: () => void;
}

const AcceptChangesButton = ({ summary, stateKey, onClick }: AcceptButtonProps) => {
  const t = useT();
  const [sent, setSent] = useRemountSafeState(stateKey, false);
  return (
    <div className="my-3 rounded-md border border-confirm-line-strong bg-confirm-soft p-3">
      <div className="text-xs uppercase tracking-wide text-confirm-fg">
        {t("chatRender.confirmationRequired")}
      </div>
      <div className="mt-1 text-sm text-ink-3">{summary}</div>
      <button
        type="button"
        className={`mt-2 ${CONFIRM_BTN} disabled:cursor-not-allowed disabled:bg-disabled-strong`}
        disabled={sent}
        onClick={() => {
          if (sent) return;
          setSent(true);
          onClick();
        }}
      >
        {sent ? t("chatRender.acceptChangesSent") : t("chatRender.acceptChanges")}
      </button>
    </div>
  );
};

/**
 * ReactMarkdown component overrides that turn a `loom-confirm` fenced block
 * into the Accept-changes button. Exported because the PM agent emits the
 * fence on ANY pm-mode session — every surface that renders its prose as
 * Markdown (this dashboard turn, FocusChat's bubbles) needs the same
 * interception, or the fence shows as an inert code block.
 */
export function loomConfirmComponents(
  entryId: string | undefined,
  onAcceptChanges?: (text: string) => void,
): Components {
  return {
    code({ className, children, ...rest }) {
      // ReactMarkdown passes the language as `language-<lang>` on the
      // inner <code> for fenced blocks. We intercept only
      // `language-loom-confirm` and let everything else fall through
      // to the default <code> render so syntax-highlighted snippets
      // stay snippets.
      if (className === "language-loom-confirm") {
        const summary = String(children).trim();
        return (
          <AcceptChangesButton
            summary={summary}
            // Keyed by the ENTRY only, deliberately not the block
            // summary: every loom-confirm button sends the identical
            // ACCEPT_REPLY, so one click consumes the message's
            // confirmation either way — and an entry-only key stays
            // stable while a replay rebuilds the message delta-by-
            // delta (a summary-suffixed key would briefly mismatch
            // the partial fence body and re-arm the button mid-replay).
            stateKey={entryId !== undefined ? `acceptchanges:${entryId}` : null}
            onClick={() => onAcceptChanges?.(ACCEPT_REPLY)}
          />
        );
      }
      return (
        <code className={className} {...rest}>
          {children as ReactNode}
        </code>
      );
    },
    // Without this, the outer <pre> for the loom-confirm block
    // still wraps our button in a dark code-pre frame. Strip the
    // pre wrapper entirely when its only child is our marker so
    // the button stands alone.
    pre({ children, ...rest }) {
      const child = Array.isArray(children) ? children[0] : children;
      if (
        child &&
        typeof child === "object" &&
        "props" in child &&
        (child.props as { className?: string }).className ===
          "language-loom-confirm"
      ) {
        return <>{children}</>;
      }
      return <pre {...rest}>{children as ReactNode}</pre>;
    },
  };
}

interface AssistantTurnProps {
  readonly text: string;
  /**
   * Timeline entry id — stable across the reconnect replay — keying the
   * remount-safe `sent` latch of any loom-confirm button in this message.
   * Optional: without it the latch degrades to mount-local state.
   */
  readonly entryId?: string;
  /**
   * Called when the user clicks an Accept-changes button. Wired to
   * `useWsSession.send` from ChatPage; the string passed is the
   * affirmative reply the agent will see in its next turn. Optional —
   * when omitted (e.g. in story-book renders) the button is rendered
   * but the click is a no-op.
   */
  readonly onAcceptChanges?: (text: string) => void;
}

export function AssistantTurn({ text, entryId, onAcceptChanges }: AssistantTurnProps) {
  const t = useT();
  return (
    <div className="rounded-lg bg-surface p-3 shadow-sm shadow-elev">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
        {t("chatRender.assistant")}
      </div>
      <div
        aria-live="polite"
        aria-atomic="false"
        className="prose prose-sm prose-slate dark:prose-invert max-w-none text-ink prose-pre:bg-code prose-pre:text-code-ink prose-code:before:content-none prose-code:after:content-none prose-code:rounded prose-code:bg-surface-3 prose-code:px-1 prose-code:py-0.5 prose-code:font-normal prose-a:text-confirm-fg-bright prose-headings:mt-3 prose-headings:mb-1 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0 prose-table:my-2"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={loomConfirmComponents(entryId, onAcceptChanges)}
        >
          {/* Under the UI public-name mode the model's "Loom" shows as the
              public name (display only; code and fences untouched). */}
          {uiProse(text)}
        </ReactMarkdown>
      </div>
    </div>
  );
}
